import { useCallback, useId, useMemo, useState } from 'react';
import Button from './Button.jsx';
import { ChevronDown, ChevronUp } from 'lucide-react';

/** Supported `titleSize` values (maps to Tailwind `text-*` utilities, no inline styles). */
export const ACCORDION_SECTION_TITLE_SIZES = ['xs', 'sm', 'md', 'lg'];

const TITLE_SIZE_SET = new Set(ACCORDION_SECTION_TITLE_SIZES);

const TITLE_SIZE_CLASS = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};

const ROOT_BASE_CLASS =
  'flex w-full min-w-0 flex-col border-b border-gray-200 last:border-0 dark:border-gray-700';

const HEADER_ROW_CLASS = 'flex w-full min-w-0 items-center justify-between';

const TITLE_BASE_CLASS = 'min-w-0 flex-1 truncate font-semibold text-content';

/**
 * Collapsible section with a header (title + chevron) and arbitrary `children`.
 *
 * @param {React.ReactNode} props.title — Header label (e.g. translated string)
 * @param {boolean} [props.defaultOpen=false] — Initial open/closed state
 * @param {React.ReactNode} props.children — Content visible only when expanded
 *
 * Uses the **parent width** (`w-full min-w-0`) so it fits flex/columns without forcing horizontal overflow.
 * **No inner padding or margin** — spacing matches Governance filter drawers; apply `className` or wrap in a padded parent.
 * @param {string} [props.className] — Extra classes on the root (Tailwind); appended **after** base utilities (margins, borders, or one-off overrides).
 * @param {'xs'|'sm'|'md'|'lg'} [props.titleSize='sm'] — Title typography (`text-xs` … `text-lg`).
 */
const AccordionSection = ({
  title,
  defaultOpen = false,
  children,
  className = '',
  titleSize = 'sm',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const titleId = useId();
  const panelId = useId();
  const Icon = open ? ChevronUp : ChevronDown;

  const resolvedTitleSize = TITLE_SIZE_SET.has(titleSize) ? titleSize : 'sm';

  const rootClassName = useMemo(
    () => [ROOT_BASE_CLASS, className].filter(Boolean).join(' '),
    [className],
  );

  const titleClassName = useMemo(
    () => [TITLE_BASE_CLASS, TITLE_SIZE_CLASS[resolvedTitleSize]].join(' '),
    [resolvedTitleSize],
  );

  const handleToggleOpen = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  return (
    <div className={rootClassName}>
      <div className={HEADER_ROW_CLASS}>
        <h3 id={titleId} className={titleClassName}>
          {title}
        </h3>
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          leftIcon={Icon}
          aria-expanded={open}
          aria-controls={panelId}
          aria-labelledby={titleId}
          onClick={handleToggleOpen}
        />
      </div>
      {open ? (
        <div id={panelId} className="min-w-0">
          {children}
        </div>
      ) : null}
    </div>
  );
};

export default AccordionSection;
